import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    InstitutionCodingBreadcrumbs,
    InstitutionCodingCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsInstitutionsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as InstitutionCodingActions from "../../../actions/institutionCoding";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { InstitutionLookupsLayout } from "../../index";

import "./InstitutionCodingPage.scss";

interface InstitutionCodingPageProps {
    form: Dtos.InstitutionCoding & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    institutionCoding: Dtos.InstitutionCoding;
    lookups: Dtos.Lookup[];
    loadingInstitutionCoding: boolean;
    loadInstitutionCodingFailure: boolean;
    loadInstitutionCodingErrorCode: string;
    savingInstitutionCoding: boolean;
    saveInstitutionCodingSuccess: boolean;
    saveInstitutionCodingFailure: boolean;
    creatingInstitutionCoding: boolean;
    createInstitutionCodingSuccess: boolean;
    validatingInstitutionCoding: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearInstitutionCoding: InstitutionCodingActions.IInstitutionCodingClearActionCreator;
    loadInstitutionCoding: InstitutionCodingActions.IInstitutionCodingLoadByIdActionCreator;
    saveInstitutionCoding: InstitutionCodingActions.IInstitutionCodingSaveActionCreator;
    createInstitutionCoding: InstitutionCodingActions.IInstitutionCodingCreateActionCreator;
    validateInstitutionCoding: InstitutionCodingActions.IInstitutionCodingValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.institutionCoding";

const InstitutionCodingPage: FC<InstitutionCodingPageProps> = ({
    form,
    formState,
    formProperties,
    institutionCoding,
    lookups,
    loadingInstitutionCoding,
    loadInstitutionCodingFailure,
    loadInstitutionCodingErrorCode,
    savingInstitutionCoding,
    saveInstitutionCodingSuccess,
    saveInstitutionCodingFailure,
    creatingInstitutionCoding,
    createInstitutionCodingSuccess,
    validatingInstitutionCoding,
    validationFailures,
    permissions,
    clearInstitutionCoding,
    loadInstitutionCoding,
    saveInstitutionCoding,
    createInstitutionCoding,
    validateInstitutionCoding,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { institutionCodingId } = useParams<{
        institutionCodingId: string;
    }>();
    const institutionCodingIdNumber = parseInt(institutionCodingId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.InstitutionCoding = {} as Dtos.InstitutionCoding;

        loadForm(
            reduxFormName,
            Object.assign({ ...institutionCoding }, extraData)
        );
    };


    // Load site location
    useEffect(() => {
        if (
            institutionCodingIdNumber &&
            !institutionCoding &&
            !loadingInstitutionCoding
        ) {
            setDrawerItems(SettingsInstitutionsSubMenu(permissions));
            setBreadcrumbs(
                InstitutionCodingBreadcrumbs(institutionCodingIdNumber)
            );
            loadInstitutionCoding(institutionCodingIdNumber);
        } else if (
            !institutionCodingIdNumber &&
            !creatingInstitutionCoding &&
            !createInstitutionCodingSuccess
        ) {
            createInstitutionCoding();
            setBreadcrumbs(InstitutionCodingCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (institutionCoding && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        institutionCodingIdNumber,
        institutionCoding,
        setDrawerItems,
        SettingsInstitutionsSubMenu,
        permissions,
        setBreadcrumbs,
        InstitutionCodingBreadcrumbs,
        loadInstitutionCoding,
        createInstitutionCoding,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingInstitutionCoding,
        creatingInstitutionCoding,
        createInstitutionCodingSuccess,
        InstitutionCodingCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionCodingFailure) {
            navigate(`/error/${loadInstitutionCodingErrorCode}`);
        }
    }, [
        loadInstitutionCodingFailure,
        loadInstitutionCodingErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (institutionCodingIdNumber && institutionCoding?.value) {
            setBreadcrumbs(
                InstitutionCodingBreadcrumbs(
                    institutionCodingIdNumber,
                    institutionCoding?.value
                )
            );
        }
    }, [
        institutionCodingIdNumber,
        institutionCoding,
        setBreadcrumbs,
        InstitutionCodingBreadcrumbs,
    ]);

    // Reload institution coding after saving
    useEffect(() => {
        if (!savingInstitutionCoding && saveInstitutionCodingSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Institution coding saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/institution/institutioncodings";
                navigate(url);
            } else if (!institutionCodingIdNumber) {
                let url: string = `/settings/institution/institutioncodings/${institutionCoding?.id}`;
                navigate(url);
                clearInstitutionCoding();
            }
        }
    }, [
        savingInstitutionCoding,
        saveInstitutionCodingSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingInstitutionCoding && saveInstitutionCodingFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingInstitutionCoding, saveInstitutionCodingFailure]);

    // Cleanup effect to clear the institution coding on component unmount
    useEffect(() => {
        return () => {
            clearInstitutionCoding();
            resetForm(reduxFormName);
        };
    }, [clearInstitutionCoding, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/institution/institutioncodings";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.InstitutionCoding) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.InstitutionCoding = {} as Dtos.InstitutionCoding;

            await saveInstitutionCoding(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.InstitutionCoding = {} as Dtos.InstitutionCoding;

            if (form && hasBeenSubmitted && !validatingInstitutionCoding) {
                await validateInstitutionCoding(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.InstitutionCodingUpdate,
                Dtos.Permission.InstitutionCodingCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingInstitutionCoding ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "institutionCoding",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "institutionCoding",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "institutionCoding",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "institutionCoding",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingInstitutionCoding,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <InstitutionLookupsLayout
            loading={loadingInstitutionCoding}
            permission={Dtos.Permission.InstitutionCodingView}
        >
            {renderContent}
        </InstitutionLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionCodingPageProps> => {
    let institutionCoding: Dtos.InstitutionCoding | undefined = !(
        state.institutionCodings.formData instanceof Array
    )
        ? state.institutionCodings.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institutionCoding: institutionCoding!,
        loadingInstitutionCoding:
            state.institutionCodings.loadState &&
            state.institutionCodings.loadState.status == RequestState.Pending,
        loadInstitutionCodingFailure:
            state.institutionCodings.loadState &&
            state.institutionCodings.loadState.status == RequestState.Failure,
        loadInstitutionCodingErrorCode:
            state.institutionCodings.loadState.errorCode,
        savingInstitutionCoding:
            state.institutionCodings.saveState &&
            state.institutionCodings.saveState.status == RequestState.Pending,
        saveInstitutionCodingSuccess:
            state.institutionCodings.saveState &&
            state.institutionCodings.saveState.status == RequestState.Success,
        saveInstitutionCodingFailure:
            state.institutionCodings.saveState &&
            state.institutionCodings.saveState.status == RequestState.Failure,
        creatingInstitutionCoding:
            state.institutionCodings.createState &&
            state.institutionCodings.createState.status == RequestState.Pending,
        createInstitutionCodingSuccess:
            state.institutionCodings.createState &&
            state.institutionCodings.createState.status == RequestState.Success,
        validatingInstitutionCoding:
            state.institutionCodings.validateState &&
            state.institutionCodings.validateState.status ==
                RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.institutionCoding,
        formState: state.institutionCodings.formState,
        formProperties: state.institutionCodings.formProperties,
        validationFailures: institutionCoding
            ? state.institutionCodings.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<InstitutionCodingPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadInstitutionCoding: bindActionCreators(
            InstitutionCodingActions.LoadInstitutionCodingById,
            dispatch
        ),
        clearInstitutionCoding: bindActionCreators(
            InstitutionCodingActions.Clear,
            dispatch
        ),
        saveInstitutionCoding: bindActionCreators(
            InstitutionCodingActions.SaveInstitutionCoding,
            dispatch
        ),
        createInstitutionCoding: bindActionCreators(
            InstitutionCodingActions.CreateInstitutionCoding,
            dispatch
        ),
        validateInstitutionCoding: bindActionCreators(
            InstitutionCodingActions.ValidateInstitutionCoding,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InstitutionCodingPage);
